import React, { useState, useEffect } from "react";
import { Modal, Select, Button, Tag, Empty, Alert } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
    getAllCoupons,
    assignCoupon,
    sendCouponAssignmentEmail,
    getUserCouponAssignments,
} from "../../Services/coupon";

function AssignCouponModal({ isModalVisible, handleCancel, initialValues }) {
    const [selectedCouponId, setSelectedCouponId] = useState(null);
    // set once "Assign" succeeds - { assignment_id, coupon_code, plan_name, user_email, email_sent }
    const [assignment, setAssignment] = useState(null);

    useEffect(() => {
        if (isModalVisible) {
            setSelectedCouponId(null);
            setAssignment(null);
        }
    }, [isModalVisible]);

    const { data, isLoading } = useQuery({
        queryKey: ["active-coupons-for-assign"],
        queryFn: () => getAllCoupons({ isActive: true, limit: 100 }),
        enabled: isModalVisible,
        refetchOnWindowFocus: false,
        retry: false,
    });

    // Coupons already assigned to this user - shown up top + used to block
    // re-assigning the same code.
    const { data: assignedData, refetch: refetchAssigned } = useQuery({
        queryKey: ["user-coupon-assignments", initialValues?.id],
        queryFn: () => getUserCouponAssignments({ user_id: initialValues?.id }),
        enabled: isModalVisible && !!initialValues?.id,
        refetchOnWindowFocus: false,
        retry: false,
    });
    const assignedCoupons = assignedData?.assignments || [];

    // Step 1: create the assignment record only - no email yet.
    const { mutate: triggerAssign, isPending: isAssigning } = useMutation({
        mutationFn: () => assignCoupon({ user_id: initialValues?.id, coupon_id: selectedCouponId }),
        onSuccess: (result) => {
            if (result) {
                setAssignment(result);
                refetchAssigned();
            }
        },
    });

    // Step 2: send (or retry/resend) the email for that assignment - kept
    // as a separate button so a failed/suppressed send can be retried
    // without creating a duplicate assignment record.
    const { mutate: triggerSendEmail, isPending: isSending } = useMutation({
        mutationFn: () => sendCouponAssignmentEmail({ assignment_id: (assignment || existingAssignment)?.assignment_id }),
        onSuccess: (result) => {
            if (result) {
                setAssignment((prev) => ({ ...(prev || existingAssignment), ...result }));
                refetchAssigned();
            }
        },
    });

    const coupons = data?.coupons || [];
    const selectedCoupon = coupons.find((c) => c.id === selectedCouponId);

    // Is the currently selected coupon already assigned to this user?
    const existingAssignment = assignedCoupons.find((a) => a.coupon_id === selectedCouponId);
    // Effective assignment = the one we just created OR the pre-existing one.
    const effectiveAssignment = assignment || existingAssignment || null;
    const isAssigned = !!effectiveAssignment;

    const handleCouponChange = (value) => {
        setSelectedCouponId(value);
        setAssignment(null);
    };

    // Title with a highlighted coupon chip + "already assigned" badge, instead
    // of a plain long string that wraps to two lines.
    const titleNode = (
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <span style={{ fontWeight: 600 }}>
                Assign Coupon{initialValues?.name ? ` - ${initialValues.name}` : ""}
            </span>
            {selectedCoupon && (
                <Tag
                    color={existingAssignment ? "green" : "geekblue"}
                    icon={existingAssignment ? <CheckCircleOutlined /> : undefined}
                    style={{ margin: 0, fontWeight: 600 }}
                >
                    {selectedCoupon.code}
                </Tag>
            )}
            {existingAssignment && (
                <span style={{ fontSize: 12, fontWeight: 400, color: "#52c41a" }}>
                    Already assigned
                </span>
            )}
        </div>
    );

    return (
        <Modal
            title={titleNode}
            open={isModalVisible}
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" onClick={handleCancel}>Close</Button>,
                <Button
                    key="assign"
                    type={isAssigned ? "default" : "primary"}
                    disabled={!selectedCouponId || isAssigned}
                    loading={isAssigning}
                    onClick={() => triggerAssign()}
                    icon={isAssigned ? <CheckCircleOutlined style={{ color: "#52c41a" }} /> : undefined}
                    style={isAssigned ? { color: "#52c41a", borderColor: "#52c41a" } : undefined}
                >
                    {isAssigned ? "Assigned" : "Assign"}
                </Button>,
                <Button
                    key="send-email"
                    // (a) primary only when enabled, so the disabled state reads as
                    // a normal greyed button, not a washed-out blue.
                    type={isAssigned && !existingAssignment?.is_redeemed ? "primary" : "default"}
                    disabled={!isAssigned || existingAssignment?.is_redeemed}
                    loading={isSending}
                    onClick={() => triggerSendEmail()}
                >
                    {effectiveAssignment?.email_sent ? "Resend Email" : "Send Email"}
                </Button>,
            ]}
            width={480}
        >
            {coupons.length === 0 && !isLoading ? (
                <Empty description="No active coupons available" />
            ) : (
                <>
                    <Select
                        style={{ width: "100%" }}
                        placeholder="Select a coupon"
                        loading={isLoading}
                        value={selectedCouponId}
                        onChange={handleCouponChange}
                        options={coupons.map((c) => ({
                            // (b) discount coupons have no plan_name (null) - show
                            // "DISCOUNT20 - Discount" instead of "DISCOUNT20 - null".
                            label: `${c.code}${c.plan_name ? ` - ${c.plan_name}` : c.coupon_type === "discount" ? " - Discount" : ""}`,
                            value: c.id,
                        }))}
                    />
                    {selectedCoupon && (
                        <div style={{ marginTop: 16 }}>
                            <p style={{ marginBottom: 8 }}>
                                <Tag color="green">{selectedCoupon.code}</Tag>
                                {selectedCoupon.coupon_type === "discount"
                                    ? " is a discount offer."
                                    : <> grants <strong>{selectedCoupon.plan_name}</strong>, completely free.</>}
                            </p>
                            {selectedCoupon.description && (
                                <p style={{ marginBottom: 8 }}>{selectedCoupon.description}</p>
                            )}
                            <p style={{ color: "gray", marginBottom: 0 }}>
                                "Assign" records the coupon for this user. "Send Email" emails them
                                the code separately - they still need to apply it themselves to
                                activate the plan.
                            </p>
                        </div>
                    )}
                    {isAssigned && (
                        <Alert
                            style={{ marginTop: 16 }}
                            // (c) redeemed is a stronger state than just assigned - flag it distinctly.
                            type={existingAssignment?.is_redeemed ? "info" : effectiveAssignment.email_sent ? "success" : "warning"}
                            showIcon
                            message={
                                existingAssignment?.is_redeemed
                                    ? `${effectiveAssignment.coupon_code} has already been redeemed by this user - it cannot be assigned again.`
                                    : assignment
                                    ? (assignment.email_sent
                                        ? `Assigned and emailed to ${assignment.user_email}.`
                                        : `Assigned to ${assignment.user_email}. Click "Send Email" to email the code.`)
                                    : `${effectiveAssignment.coupon_code} is already assigned to this user.${effectiveAssignment.email_sent ? " Email already sent - use \"Resend Email\" if needed." : " Click \"Send Email\" to email the code."}`
                            }
                        />
                    )}
                </>
            )}
        </Modal>
    );
}

export default AssignCouponModal;
